import { useCallback, useContext, useMemo } from 'react'
import { Context } from '../context/AuthContext'
import { temAlgumaPermissaoDoSistema } from '../const/permissoes'

/**
 * Autorização na interface, a partir das permissões do SSO.
 *
 * Esconder o botão é conveniência, não segurança: a decisão que vale é a da
 * API, que aplica o mesmo catálogo de permissões em todas as rotas. Aqui a
 * função é não oferecer ao usuário uma ação que ele receberia 403 ao tentar.
 *
 * **Modo de transição:** enquanto o usuário não tiver NENHUMA permissão deste
 * aplicativo no SSO, a tela mostra tudo. Sem isso, um usuário legítimo veria a
 * interface vazia enquanto o catálogo não estivesse atribuído. O modo se
 * encerra sozinho no momento em que a primeira permissão chegar — o mesmo
 * critério usado no back-end, para os dois não discordarem.
 */

/** Roles transversais do SSO que valem como administrador aqui. */
const ROLES_ADMINISTRADORAS = ['admin', 'administrador']

const normalizar = valor => String(valor ?? '').trim().toLowerCase()

export const usePermissoes = () => {
  const { authenticated, permissions, userRoles } = useContext(Context)

  const lista = useMemo(
    () => (permissions || []).map(normalizar),
    [permissions]
  )

  const ehAdministrador = useMemo(
    () => (userRoles || []).some(r => ROLES_ADMINISTRADORAS.includes(normalizar(r))),
    [userRoles]
  )

  const emTransicao = useMemo(() => !temAlgumaPermissaoDoSistema(lista), [lista])

  const pode = useCallback(
    (...permissoes) => {
      if (!authenticated) return false
      if (ehAdministrador || emTransicao) return true
      return permissoes.some(p => lista.includes(normalizar(p)))
    },
    [authenticated, ehAdministrador, emTransicao, lista]
  )

  return { pode, ehAdministrador, emTransicao }
}

/**
 * Envolve um trecho de UI e só o renderiza quando o usuário tem a permissão.
 *
 * Aceita `permissao` (uma ou várias) — sem nenhuma, basta estar autenticado.
 */
const PermissionComponent = ({ permissao, children }) => {
  const { pode } = usePermissoes()
  const { authenticated } = useContext(Context)

  if (!authenticated) return null
  if (!permissao) return children

  const exigidas = Array.isArray(permissao) ? permissao : [permissao]
  return pode(...exigidas) ? children : null
}

/** Versão em hook, para desabilitar controle em vez de escondê-lo. */
export const usePode = (...permissoes) => {
  const { pode } = usePermissoes()
  return pode(...permissoes)
}

export default PermissionComponent
